import { AsyncPipe, CommonModule } from '@angular/common';
import { AfterViewInit, Component, forwardRef, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { WizardComponent, WizardStepComponent } from 'mbs-ui-kit';
import { HelpService } from 'mbs-ui-shared@help/help.service';
import { AgentType } from 'mbs-ui-shared@models/Computer';
import { PlanTypes } from 'mbs-ui-shared@models/PlanTypes.enum';
import { noop } from 'rxjs';
import { RestoreVMWareModelForStepsHelper } from 'wizards/helpers/restore-vm-ware-helpers/restore-vm-ware-model-for-steps-helper';
import { RestoreVMWarePlanFromStepsHelpers } from 'wizards/helpers/restore-vm-ware-helpers/restore-vm-ware-plan-from-steps-helpers';
import { RestoreType } from 'wizards/models/restore-point-models';
import { RestoreVMWareFormModel } from 'wizards/models/restoreVMWare/restore-vm-ware-form-model';
import { RestoreAsDiskEnum } from 'wizards/models/type-data-models';
import { WizardHelpLink } from 'wizards/remote-management-wizards/strategy/wizard-help-link.class';
import { WizardBaseClass } from 'wizards/remote-management-wizards/wizard-base.class';
import { WindowsFeatures, WizardStepsService } from 'wizards/services/wizard-steps.service';
import { WizardsServicesHubService } from 'wizards/services/wizards-services-hub.service';
import { StepLoadInfo } from 'wizards/steps/StepBase.class';
import { BackupToRestoreStepComponent } from 'wizards/steps/backup-to-restore-step/backup-to-restore-step.component';
import { EncryptionOptionsStepComponent } from 'wizards/steps/encription-options-step/encryption-options-step.component';
import { NotificationsStepComponent } from 'wizards/steps/notifications-step/notifications-step.component';
import { DestinationVMStepComponent } from 'wizards/steps/only-restore-steps/destination-vm-step/destination-vm-step.component';
import { RestoreDisksStepComponent } from 'wizards/steps/only-restore-steps/restore-disks-step/restore-disks-step.component';
import { TypeDataStepComponent } from 'wizards/steps/only-restore-steps/type-data-step/type-data-step.component';
import { PlanNameStepComponent } from 'wizards/steps/plan-name-step/plan-name-step.component';
import { PrePostActionsStepComponent } from 'wizards/steps/pre-post-actions-step/pre-post-actions-step.component';
import { RestorePointStepComponent } from 'wizards/steps/restore-point-step/restore-point-step.component';
import { RestoreSourceStepComponent } from 'wizards/steps/restore-source-step/restore-source-step.component';
import { ScheduleAdvancedStepComponent } from 'wizards/steps/schedule-advanced-step/schedule-advanced-step.component';
import { ScheduleStepComponent } from 'wizards/steps/schedule-step/schedule-step.component';
import { SelectComputerStepComponent } from 'wizards/steps/select-computer-step/select-computer-step.component';
import { SelectHostStepComponent } from 'wizards/steps/select-host-step/select-host-step.component';
import { WhereBackupStepComponent } from 'wizards/steps/where-backup-step/where-backup-step.component';

type RestoreVMWareWizardHelpDocumentationLinks =
  | 'welcome'
  | 'storage'
  | 'selectComputer'
  | 'selectBackup'
  | 'selectPoint'
  | 'source'
  | 'typeOfData'
  | 'disks'
  | 'host'
  | 'destination'
  | 'encryption'
  | 'schedule'
  | 'prePost'
  | 'notification';

@UntilDestroy()
@Component({
  selector: 'mbs-restore-vmware-wizard',
  templateUrl: './restore-vmware-wizard.component.html',
  providers: [
    {
      provide: WizardBaseClass,
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      useExisting: forwardRef(() => RestoreVMWareWizardComponent)
    }
  ],
  imports: [
    AsyncPipe,
    BackupToRestoreStepComponent,
    CommonModule,
    DestinationVMStepComponent,
    EncryptionOptionsStepComponent,
    FormsModule,
    NotificationsStepComponent,
    PlanNameStepComponent,
    PrePostActionsStepComponent,
    RestoreDisksStepComponent,
    RestorePointStepComponent,
    RestoreSourceStepComponent,
    ScheduleAdvancedStepComponent,
    ScheduleStepComponent,
    SelectComputerStepComponent,
    SelectHostStepComponent,
    TypeDataStepComponent,
    WhereBackupStepComponent,
    WizardComponent,
    WizardStepComponent,
    I18NextPipe
  ]
})
export class RestoreVMWareWizardComponent extends WizardBaseClass implements AfterViewInit {
  i18nPipe: I18NextPipe;

  public readonly restoreType = RestoreType;
  public readonly restoreAsDiskEnum = RestoreAsDiskEnum;
  public readonly restorePointTitle = this.i18nPipe.transform('wizards:restore_point_title');
  public readonly hostTitle = this.i18nPipe.transform('wizards:select_host_step_title');

  public helpLink: WizardHelpLink<RestoreVMWareWizardHelpDocumentationLinks>;

  constructor() {
    const i18nPipe = inject(I18NextPipe);
    const services = inject(WizardsServicesHubService);
    const stepService = inject(WizardStepsService);
    const helpService = inject(HelpService);

    super();
    this.i18nPipe = i18nPipe;

    this.FormModel = new RestoreVMWareFormModel(this.isNBF);

    this.initSubscribes();
  }

  protected updateStepData(plan, isDefault?: boolean): void {
    this.setLoadedPlanAndRunChainRequestsForBackup(plan);

    if (isDefault) return this.setDefaultValue();

    this.FormModel = RestoreVMWareModelForStepsHelper.restoreVMModelFromPlan(plan, this.defaultSettings());

    this.stepsManager.showEncryptionOptionsStepIfNeed();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected loadInfoHandlerFinally(event?: StepLoadInfo): void {
    this.cdr.detectChanges();
  }

  ngAfterViewInit() {
    this.checkVMPlugin();
  }

  private checkVMPlugin(): void {
    this.stepService
      .VMFeatureTest(this.mainService.hid, WindowsFeatures.vMware)
      .pipe(untilDestroyed(this))
      .subscribe((res) => {
        if (res?.data === false) this.installVMFeature();
      });
  }

  private installVMFeature(): void {
    this.stepService
      .getRemoteCommandData(
        {
          agentType: AgentType.Backup,
          commandType: 'InstallFeature',
          params: { Feature: WindowsFeatures.vMware }
        },
        this.mainService.hid
      )
      .subscribe(noop);
  }

  restoreAsChangeHandler(type: RestoreAsDiskEnum): void {
    if (type === RestoreAsDiskEnum.Virtual) {
      this.mainService.type = PlanTypes.RestoreVMwareToCustom;
      this.planType = PlanTypes.RestoreVMwareToCustom;
    } else {
      this.mainService.type = PlanTypes.RestoreVMware;
      this.planType = PlanTypes.RestoreVMware;
    }
    this.cdr.detectChanges();
  }

  protected getPlanForSaving(): any {
    return RestoreVMWarePlanFromStepsHelpers.getUpdatedPlan(
      this.loadedPlan.Plan || this.loadedPlan.plan,
      this.FormModel,
      this.defaultSettings()
    );
  }

  getRAWHelpLinks(): Record<RestoreVMWareWizardHelpDocumentationLinks, string> {
    return {
      welcome: 'restore/vmware/restore-console#planname',
      storage: 'restore/vmware/restore-console#storage',
      selectComputer: 'restore/vmware/restore-console#comp',
      selectBackup: 'restore/vmware/restore-console#backup',
      selectPoint: 'restore/vmware/restore-console#rp',
      source: 'restore/vmware/restore-console#rs',
      typeOfData: 'restore/vmware/restore-console#restoreas',
      disks: 'restore/vmware/restore-console#partitions',
      host: 'restore/vmware/restore-console#host',
      destination: 'restore/vmware/restore-console#destination',
      encryption: 'restore/vmware/restore-console#encryption',
      schedule: 'restore/vmware/restore-console#schedule',
      prePost: 'restore/vmware/restore-console#prepost',
      notification: 'restore/vmware/restore-console#notification'
    };
  }
}
